import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { formatBRL } from '@/lib/format';
import type { ReferralStatsOut } from '@/api/referral';

/**
 * Card "Indique e ganhe" da Carteira (logo abaixo da carteira cashback).
 * Borda-gradiente do espectro, mesma linguagem do card de cashback.
 *
 * Componente PURO (props) pra render SSR nos smoke tests — o fetch, o
 * clipboard e o share ficam no pai (Wallet). `shareHref` preenchido = sem
 * share nativo → botão vira âncora `wa.me` (gesto viaja na âncora).
 */

export const REFERRAL_TEXT = '#A23C92';
const SPECTRUM_GRADIENT =
  'linear-gradient(135deg,#EE263E,#DE7C00,#FFB92A,#0090E2,#835DD0,#C04CAE)';

export interface ReferralCardProps {
  code: string;
  referrerPercent: string;
  welcomeBrl: string;
  windowDays: number;
  stats: ReferralStatsOut;
  copied: boolean;
  /** Âncora WhatsApp (sem share nativo). null → usa `onShare`. */
  shareHref: string | null;
  onCopy: () => void;
  onShare: () => void;
}

export function ReferralCard({
  code,
  referrerPercent,
  welcomeBrl,
  windowDays,
  stats,
  copied,
  shareHref,
  onCopy,
  onShare
}: ReferralCardProps) {
  const summary =
    stats.claimed > 0
      ? `${stats.claimed} ${stats.claimed === 1 ? 'indicado' : 'indicados'} · ` +
        `${stats.earning} rendendo · ${formatBRL(stats.earned_brl)} ganhos`
      : 'Acompanhe seus indicados';

  return (
    <div
      className="rounded-[22px] shadow-soft"
      style={{ padding: '1.5px', background: SPECTRUM_GRADIENT }}
    >
      <div className="flex flex-col gap-3 rounded-[21px] bg-white px-[18px] py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <span
              className="text-[11.5px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: REFERRAL_TEXT }}
            >
              Indique e ganhe
            </span>
            <span
              className="leading-[1.15] text-bow-royal"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 17,
                letterSpacing: '-0.02em'
              }}
            >
              <strong style={{ color: REFERRAL_TEXT, fontWeight: 600 }}>
                {referrerPercent}% de cashback
              </strong>{' '}
              de tudo que seus amigos carregarem por {windowDays} dias
            </span>
            <span className="text-[12px] leading-snug" style={{ color: 'rgba(0,27,116,0.6)' }}>
              Eles ganham {formatBRL(welcomeBrl)} no primeiro aporte.
            </span>
          </div>
          <Link
            to="/indique"
            aria-label="Ver detalhes do programa"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
            style={{ background: 'linear-gradient(135deg,#835DD0,#C04CAE)' }}
          >
            <GiftGlyph />
          </Link>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-2xl bg-bow-cream px-4 py-3">
          <span
            className="truncate text-[18px] font-bold text-bow-royal"
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.08em' }}
          >
            {code}
          </span>
          <button
            type="button"
            onClick={onCopy}
            className="shrink-0 text-[13px] font-semibold text-bow-royal underline-offset-2 hover:underline"
          >
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
        </div>

        {shareHref ? (
          <a
            href={shareHref}
            target="_blank"
            rel="noreferrer"
            className="block no-underline"
          >
            <Button variant="accent" size="md" type="button">
              <WhatsAppGlyph /> Compartilhar convite
            </Button>
          </a>
        ) : (
          <Button variant="accent" size="md" type="button" onClick={onShare}>
            <WhatsAppGlyph /> Compartilhar convite
          </Button>
        )}

        <Link
          to="/indique"
          className="text-center text-[12.5px] font-semibold underline-offset-2 hover:underline"
          style={{ color: 'rgba(0,27,116,0.6)' }}
        >
          {summary} ›
        </Link>
      </div>
    </div>
  );
}

export function WhatsAppGlyph({ size = 16 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.78h-.01a9.86 9.86 0 0 1-5.02-1.37l-.36-.21-3.73.98 1-3.64-.24-.37a9.82 9.82 0 0 1-1.51-5.24c0-5.43 4.42-9.85 9.86-9.85 2.63 0 5.1 1.03 6.96 2.89a9.79 9.79 0 0 1 2.88 6.97c0 5.43-4.42 9.85-9.85 9.85m8.38-18.23A11.8 11.8 0 0 0 12.04 0C5.5 0 .17 5.33.17 11.87c0 2.09.55 4.14 1.58 5.94L0 24l6.33-1.66a11.86 11.86 0 0 0 5.68 1.45h.01c6.54 0 11.87-5.33 11.88-11.88 0-3.17-1.24-6.15-3.48-8.39" />
    </svg>
  );
}

function GiftGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
  );
}
